import React, { useRef, useState, useEffect } from 'react';
import { View, Text, Animated, ScrollView } from 'react-native';
import i18n from 'i18n-js';
import moment from 'moment';
import { connect } from 'react-redux';

import { scale, width } from '@components/frame';
import { isPhone, isWeb, navigateTo, localTime, capitalizeFirstLetter, controlAPICall, addQueryParams } from '@helpers';
import Line from '@components/common/line';

import { getBoxShadowStyle } from '@styles/shadow';
import { font } from '@styles/fonts';

import Chart from './components/chart';
import Arrow from './components/arrow';
import NcFileItem from './components/ncFileItem';

import Selector from '@styles/selectors';

import { getNcFileStatsMonth, getNcFileStatsDay, getNcFileStatsHour, getMachineStatisticsForNCFile } from '@reducers/machines';
import { generateImageURL } from '@reducers/blobStorage';

import { sendLog } from '@reducers/dataDogs';

import FeatureFlags, { FeatureFlagsAvailable } from '@components/featureFlags';

import Paid from '@components/ncFiles/components/paid/index';
import { useRoute } from '@react-navigation/native';
import { MachinesScreen, TimePeriod } from '@v2/pages/machines/common/enums';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

const EventsChart = ({
  C,
  getMachineStatisticsForNCFile,
  setSelectedTime,
  selectedChartDate,
  setSelectedChartDate,
  id,
  shortName,
  setInitialEventDate,
  getNcFileStatsMonth,
  getNcFileStatsDay,
  getNcFileStatsHour,
  sendLog,
  chartData: data,
  setChartData: setData,
  machineStatisticsForNCFile,
  generateImageURL,
  allSelected,
  isManufacture,
  featureFlags,
}) => {
  const abortControllerMachine = useRef();
  const route = useRoute();
  const { timePeriod: selectedTime } = useHandleParams();

  const [ncFiles, setNcFiles] = useState([]);
  const [dataPrev, setDataPrev] = useState({});

  const [showLine, setShowLineInternal] = useState(route?.params?.chart ? route?.params?.chart === 'line' : true);
  const setShowLine = (data) => {
    addQueryParams({ chart: data ? 'line' : 'bar' });
    setShowLineInternal(data);
  };

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const levelTabs = [
    {
      name: MachinesScreen.PRODUCTIVE,
    },
    {
      name: MachinesScreen.ACTIVE,
    },
  ];

  const displayPaid = FeatureFlagsAvailable('ncfile-paid', featureFlags);

  if (displayPaid) {
    levelTabs.unshift({ name: MachinesScreen.PAID });
  }

  const isActive = route.name.includes(MachinesScreen.ACTIVE);

  const navigationData = {
    initialTab: 'ncFiles',
    search: JSON.stringify({
      id,
      start: data.start,
      end: data.end,
      shortName,
      files: ncFiles.map((i) => i.NcFileMain).join(),
    }),
  };

  const updateType = [
    {
      name: `Update NC File Type`,
      alert: ncFiles.length,
      onClick: () => {
        navigateTo(
          'SearchScreen',
          isWeb
            ? navigationData
            : {
                screen: 'Search',
                params: navigationData,
              }
        );
      },
    },
  ];

  const fullWidth = isPhone ? width - scale(24) : scale(532, false);
  const fullHeight = isActive && !showLine ? scale(243, false) : scale(276, false);

  const toGo = (selected, configOnly = false) => {
    const nextTime = {
      year: 'month',
      month: 'day',
      week: 'day',
    };

    if (configOnly) {
      return {
        section: nextTime[data.selectedTime],
        date: selected.dateISO,
        manufacture: isManufacture,
        ncFileId: id,
        ncFile: route?.params?.ncFile,
        noMachine: true,
        status: route.name,
        period: route?.params?.period,
        chart: showLine ? 'line' : 'bar',
      };
    }

    if (data.selectedTime === TimePeriod.DAY) {
      setInitialEventDate(moment.utc(selected.dateISO).add(1, 'h').format());
      return;
    }
    setSelectedChartDate(selected.dateISO);
    setSelectedTime(nextTime[data.selectedTime]);
  };

  const changeDate = (action) => {
    let currentDate = moment();

    if (selectedChartDate) {
      currentDate = moment(selectedChartDate);
    }

    if (action === 'add') {
      currentDate.add(1, data.selectedTime);
    } else {
      currentDate.subtract(1, data.selectedTime);
    }

    if (currentDate.isAfter(moment())) {
      return;
    }
    setData({});
    setSelectedChartDate(currentDate.format());
  };

  const isDay = selectedTime === TimePeriod.DAY;

  useEffect(() => {
    setSelectedIndex(null);
    setSelectedItem(null);

    let momentStart = moment();
    if (selectedChartDate) {
      momentStart = moment(selectedChartDate);
    }

    const d = { period: selectedTime, date: momentStart.format(), ncFileId: id };
    controlAPICall(getMachineStatisticsForNCFile, d, abortControllerMachine);
  }, [selectedTime, selectedChartDate, id]);

  return (
    <View style={[isWeb && { flex: 1 }]}>
      <Animated.ScrollView showsVerticalScrollIndicator={false}>
        <View
          style={[
            {
              width: isPhone ? '100%' : scale(isWeb ? 632 : 450),
            },
          ]}
        >
          <View>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              {isWeb && <Arrow {...{ C, onPress: () => changeDate('subtract') }} />}
              <Chart
                {...{
                  C,
                  setData,
                  setDataPrev,
                  setNcFiles,
                  data,
                  dataPrev,
                  setSelectedIndex,
                  setSelectedItem,
                  fullWidth,
                  fullHeight,
                  getNcFileStatsMonth,
                  getNcFileStatsDay,
                  getNcFileStatsHour,
                  changeDate,
                  toGo,
                  id,
                  selectedChartDate,
                  selectedIndex,
                  selectedItem,
                  showLine,
                  setShowLine,
                  isActive,
                  sendLog,
                  setSelectedChartDate,
                  isDay,
                  allSelected,
                }}
              />
              {isWeb && !moment(data.start).add(1, data.selectedTime).isAfter(moment()) && (
                <Arrow {...{ C, rotate: 180, onPress: () => changeDate('add') }} />
              )}
            </View>
            <View style={{ width: '100%', paddingLeft: scale(isWeb ? 64 : 14), paddingTop: scale(6), minHeight: scale(20) }}>
              <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>
                {i18n.t('Updated today at')} {localTime().format('HH:00')}.{' '}
                {i18n.t('Swipe to change {{period}}', { period: i18n.t(capitalizeFirstLetter(selectedTime)) || '' })}
              </Text>
            </View>
          </View>
          {Boolean(ncFiles.length) && isDay && (
            <FeatureFlags {...{ name: 'ncfile-update-nc-file-type-search-list' }}>
              <View>
                <View
                  style={[
                    {
                      marginTop: scale(20),
                      width: '100%',
                      alignItems: 'center',
                    },
                  ]}
                >
                  <View style={[getBoxShadowStyle({ type: 'Field', C }), { width: fullWidth, borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
                    {updateType.map((i, index) => (
                      <Line
                        {...{
                          key: i.name,
                          item: i,
                          index,
                          array: updateType,
                          C,
                          smallSize: true,
                          topBorder: false,
                          roundTop: true,
                          roundBottom: true,
                          textColor: 'Link/Light/Primary',
                        }}
                      />
                    ))}
                  </View>
                </View>
                <View style={{ width: '100%', paddingHorizontal: scale(isWeb ? 64 : 14), paddingTop: scale(3) }}>
                  <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>
                    {i18n.t('In the selected period {{amount}} NC Files with unknown NC File Type were detected.', { amount: ncFiles.length })}
                  </Text>
                </View>
              </View>
            </FeatureFlags>
          )}
          <View style={{ width: '100%', alignItems: 'center' }}>
            <View style={{ width: fullWidth }}>
              {Boolean(machineStatisticsForNCFile?.data?.length) && (
                <NcFileItem {...{ machineStatisticsForNCFile, C, generateImageURL, selectedChartDate, allSelected }} />
              )}
            </View>
          </View>
        </View>
      </Animated.ScrollView>
    </View>
  );
};
const MainWrapper = (props) => {
  const route = useRoute();
  const { featureFlags, isManufacture } = props;

  const levelTabs = [
    {
      name: MachinesScreen.PRODUCTIVE,
    },
    {
      name: MachinesScreen.ACTIVE,
    },
  ];

  const displayPaid = FeatureFlagsAvailable('ncfile-paid', featureFlags);

  if (displayPaid) {
    levelTabs.unshift({ name: MachinesScreen.PAID });
  }

  const renderContent = () => {
    if (route.name.includes(MachinesScreen.PAID)) {
      return <Paid key="Paid" {...props} />;
    }

    return <EventsChart {...props} />;
  };

  return (
    <View
      style={[
        {
          alignItems: 'center',
          width: '100%',
          paddingHorizontal: scale(isPhone ? 12 : 0),
          flex: 1,
        },
      ]}
    >
      <View style={{ flexDirection: 'row', marginBottom: scale(isPhone ? 11 : 20), marginTop: scale(isPhone ? (isManufacture ? 17 : 10) : 0) }}>
        {levelTabs.map((item) => (
          <View key={item.name}>
            <Selector
              {...{
                scale,
                font,
                selected: route.name.includes(item.name),
                variant: 'L',
                text: i18n.t(item.name),
                onPress: () => {},
              }}
            />
          </View>
        ))}
      </View>
      {renderContent()}
    </View>
  );
};

export default connect(({ machines, time, user: { featureFlags } }) => ({ machines, time, featureFlags }), {
  getNcFileStatsMonth,
  getNcFileStatsDay,
  getNcFileStatsHour,
  sendLog,
  getMachineStatisticsForNCFile,
  generateImageURL,
})(MainWrapper);
